/**
 * MainLayout — Shell-Gerüst der Plattform-Shell: EINE konsolidierte dunkle
 * Kopfleiste (TopBar — Collapse-Burger, Wortmarke, Suche, AppSwitcher,
 * EnvBadge, User, Abmelden) + Sidebar (aufklappbare Gruppen) + <main>
 * (einziger Scroll-Container). Design-Korrektur ggü. der Personal-Vorlage:
 * KEINE zweite helle Header-Zeile — die Vorlage hatte dort testweise zwei
 * Kopfzeilen übereinander (Fehler, dort inzwischen behoben). Portiert aus
 * rmnc-personal (docs/SHELL_PORTIERUNG.md), ersetzt die bisherige
 * Layout()-Funktion in App.tsx.
 */
import { Suspense, useEffect, useState } from "react"
import { Outlet, useLocation } from "react-router-dom"
import { Sidebar, TECHNIK_ACCENT } from "./Sidebar"
import { TopBar } from "./TopBar"
import { CommandPalette } from "@/components/CommandPalette"
import { useLinks } from "@/components/AppSwitcher"
import { TooltipProvider } from "@/components/ui-shell/tooltip"

/** localStorage-Schlüssel für den Einklapp-Zustand der Sidebar. */
const COLLAPSED_STORAGE_KEY = "rmnc-technik.sidebar.collapsed"

// document.title je Route (UX_KONZEPT.md §4 Punkt 14) — Präfix-Match reicht,
// da Detailrouten (":id") denselben Seitentitel wie ihre Liste tragen.
const TITEL_PRAEFIXE: readonly [string, string][] = [
  ["/dashboard", "Übersicht"],
  ["/maschinen/pruefungen", "Prüfungen"],
  ["/maschinen/wartungen", "Wartung & Inspektion"],
  ["/maschinen/ersatzteile", "Ersatzteile"],
  ["/maschinen/objekte", "Objekt"],
  ["/maschinen/komponenten", "Komponenten"],
  ["/maschinen", "Anlagen"],
  ["/timetab/historie", "Zeitbuchungen"],
  ["/timetab", "Zeit erfassen"],
  ["/tickets", "Servicetickets"],
  ["/ereignis/dashboard", "Ereignis-Dashboard"],
  ["/ereignis", "Ereignismeldung"],
  ["/kvp", "KVP"],
]

function seitentitel(pathname: string): string {
  const treffer = TITEL_PRAEFIXE.find(([praefix]) => pathname.startsWith(praefix))
  return treffer ? treffer[1] : "RMNC Technik"
}

export function MainLayout() {
  const location = useLocation()
  const links = useLinks()
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_STORAGE_KEY) === "1"
    } catch {
      return false
    }
  })
  const [paletteOpen, setPaletteOpen] = useState(false)

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, sidebarCollapsed ? "1" : "0")
    } catch {
      /* Privacy-Mode: Zustand bleibt in-memory */
    }
  }, [sidebarCollapsed])

  useEffect(() => {
    document.title = `${seitentitel(location.pathname)} — RMNC Technik`
  }, [location.pathname])

  // Globaler Hotkey ⌘K (Mac) / Strg-K (Win/Linux) — Toggle der Palette.
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setPaletteOpen((open) => !open)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  return (
    <TooltipProvider>
      <div className="flex h-screen flex-col overflow-hidden bg-slate-50">
        <TopBar
          onOpenPalette={() => setPaletteOpen(true)}
          onToggleSidebar={() => setSidebarCollapsed((c) => !c)}
        />

        <div className="flex flex-1 overflow-hidden">
          <Sidebar collapsed={sidebarCollapsed} accent={TECHNIK_ACCENT} />

          {/* Volle Breite wie in rmnc-personal — die Grids sollen den Platz nutzen */}
          <main className="w-full flex-1 overflow-auto px-6 py-8">
            <Suspense fallback={<div className="p-8 text-center text-slate-400">Lädt …</div>}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} links={links} />
    </TooltipProvider>
  )
}
